import React from "react";
import {
  Box,
  Heading,
  HStack,
  VStack,
  Select,
  Text,
  Button,
  Container,
  SimpleGrid,
  Badge,
  Divider,
  Spacer,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  useColorModeValue,
  Wrap,
  Tag,
  TagLabel,
  TagCloseButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Input,
  Checkbox,
} from "@chakra-ui/react";
import { listForms, listTechnicians } from "../../../utils/formsApi";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, AreaChart, Area, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { useNavigate } from "react-router-dom";
import ModernTable from "../../../components/tables/ModernTable";
import { FiArrowLeft, FiCopy, FiDownload, FiFilter, FiUsers, FiDollarSign, FiClock, FiTrendingUp, FiCheckCircle, FiXCircle, FiTool, FiAlertTriangle } from "react-icons/fi";

export default function FormsTechStats() {
  const [techs, setTechs] = React.useState([]);
  const [techId, setTechId] = React.useState("");
  const [scope, setScope] = React.useState("included"); // included | solo
  const [primaryForms, setPrimaryForms] = React.useState([]);
  const [allForms, setAllForms] = React.useState([]);
  const [period, setPeriod] = React.useState("12");
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [payFilter, setPayFilter] = React.useState("all"); // all, paid, unpaid
  const [deliveredFilter, setDeliveredFilter] = React.useState("all"); // all, yes, no
  const [followFilter, setFollowFilter] = React.useState("all"); // all, yes, no
  const [includeNoFee, setIncludeNoFee] = React.useState(true);
  const [minFee, setMinFee] = React.useState("");
  const [maxFee, setMaxFee] = React.useState("");

  const [summary, setSummary] = React.useState({
    forms: 0,
    hours: 0,
    avgMinutes: 0,
    formsPerHour: 0,
    feeSum: 0,
    avgFee: 0,
    tryPerHour: 0,
    paid: 0,
    unpaid: 0,
    payRate: 0,
    followCount: 0,
    followRate: 0,
    deliveredCount: 0,
    deliveredRate: 0,
    surveyAvg: 0,
    lowSurveyCount: 0,
  });

  const [monthlyForms, setMonthlyForms] = React.useState([]);
  const [monthlyFees, setMonthlyFees] = React.useState([]);
  const [monthlyHours, setMonthlyHours] = React.useState([]);
  const [monthlyPayRate, setMonthlyPayRate] = React.useState([]);
  const [monthlySurveyAvg, setMonthlySurveyAvg] = React.useState([]);
  const [weekdayDist, setWeekdayDist] = React.useState([]);
  const [hourBucketDist, setHourBucketDist] = React.useState([]);
  const [opTypes, setOpTypes] = React.useState([]);
  const [topCustomers, setTopCustomers] = React.useState([]);
  const [topProjects, setTopProjects] = React.useState([]);
  const [formsTable, setFormsTable] = React.useState([]);
  const [teamMapArr, setTeamMapArr] = React.useState([]);
  const [unpaidReasons, setUnpaidReasons] = React.useState([]);
  const [opAvgTime, setOpAvgTime] = React.useState([]);
  const [deviceTime, setDeviceTime] = React.useState([]);
  const [qAverages, setQAverages] = React.useState({ q1: 0, q2: 0, q3: 0, q4: 0 });
  const navigate = useNavigate();

  const currentTechName = techs.find((t) => String(t.id) === String(techId))?.name || "";

  const toMonthKey = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
  };

  const getPrimaryTechId = React.useCallback((f) => {
    const v = f?.technician?.id ?? f?.technician_id ?? f?.technician ?? f?.technicianId;
    if (v == null) return null;
    return String(v);
  }, []);

  const normalizeText = React.useCallback((s) => {
    return String(s || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/i̇/g, "i")
      .replace(/ı/g, "i")
      .replace(/\s+/g, " ");
  }, []);

  const isInTeam = React.useCallback(
    (f) => {
      if (!Array.isArray(f?.team) || !f.team.length) return false;
      const tid = String(techId);
      const nameNorm = normalizeText(currentTechName);
      return f.team.some((t) => {
        if (!t) return false;
        if (typeof t === "number" || typeof t === "string") return String(t) === tid;
        if (t.id != null && String(t.id) === tid) return true;
        if (t.name && nameNorm && normalizeText(t.name) === nameNorm) return true;
        return false;
      });
    },
    [techId, currentTechName, normalizeText]
  );

  const isPrimary = React.useCallback(
    (f) => {
      const pid = getPrimaryTechId(f);
      return pid != null && pid === String(techId);
    },
    [getPrimaryTechId, techId]
  );

  const isIncluded = React.useCallback(
    (f) => {
      if (isPrimary(f)) return true;
      return isInTeam(f);
    },
    [isPrimary, isInTeam]
  );

  const isSolo = React.useCallback(
    (f) => {
      if (!isPrimary(f)) return false;
      if (!Array.isArray(f?.team) || !f.team.length) return true;
      const tid = String(techId);
      const others = f.team.filter((t) => t && String(t.id) !== tid);
      return others.length === 0;
    },
    [isPrimary, techId]
  );

  const titleCaseTr = React.useCallback((s) => {
    const raw = String(s || "").replace(/_/g, " ").trim();
    if (!raw) return "Bilinmiyor";
    return raw
      .split(/\s+/g)
      .filter(Boolean)
      .map((w) => w.charAt(0).toLocaleUpperCase("tr-TR") + w.slice(1).toLocaleLowerCase("tr-TR"))
      .join(" ");
  }, []);

  const opLabelForKey = React.useCallback((k) => {
    const cleaned = normalizeText(String(k || "").replace(/_/g, " "));
    const mapTo = {
      "ariza onarim": "Arıza Onarım",
      "diger": "Diğer",
      "sistem devreye alma": "Sistem Devreye Alma",
      "egitim": "Eğitim",
      "ek urun montaji": "Ek Ürün Montajı",
      "yazilim guncelleme": "Yazılım Güncelleme",
      "urun degisimi": "Ürün Değişimi",
      "teknik destek": "Teknik Destek",
      "periyodik bakim": "Periyodik Bakım",
      "kesif": "Keşif",
    };
    if (mapTo[cleaned]) return mapTo[cleaned];
    return titleCaseTr(String(k || "").replace(/_/g, " "));
  }, [normalizeText, titleCaseTr]);

  const getSurveyAvg = React.useCallback((row) => {
    const s = row?.survey || {};
    const vals = [s.q1, s.q2, s.q3, s.q4].filter((v) => typeof v === "number" && v > 0);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, []);

  const parseMinutes = React.useCallback((f) => {
    try {
      const dstr = f.service_date || f.created_at;
      const sstr = f.service_start_time;
      const estr = f.service_end_time;
      const bd = new Date(dstr);
      if (!bd || isNaN(bd.getTime())) return null;
      const rx = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
      if (typeof sstr !== "string" || typeof estr !== "string") return null;
      const sm = sstr.match(rx), em = estr.match(rx);
      if (!sm || !em) return null;
      const s = new Date(bd.getTime());
      s.setHours(Number(sm[1] || 0), Number(sm[2] || 0), Number(sm[3] || 0), 0);
      const e = new Date(bd.getTime());
      e.setHours(Number(em[1] || 0), Number(em[2] || 0), Number(em[3] || 0), 0);
      const diff = e.getTime() - s.getTime();
      if (isNaN(diff) || diff <= 0) return null;
      const minutes = Math.round(diff / 60000);
      if (minutes > 12 * 60) return null;
      return minutes;
    } catch (_) { return null; }
  }, []);

  const weekdayLabel = React.useCallback((dt) => {
    const map = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
    if (!(dt instanceof Date) || isNaN(dt.getTime())) return null;
    return map[dt.getDay()] || null;
  }, []);

  const hourBucketLabel = React.useCallback((timeStr) => {
    if (typeof timeStr !== "string") return null;
    const m = String(timeStr).match(/^(\d{1,2}):(\d{2})/);
    if (!m) return null;
    const h = Number(m[1]);
    if (isNaN(h) || h < 0 || h > 23) return null;
    if (h < 6) return "00-06";
    if (h < 9) return "06-09";
    if (h < 12) return "09-12";
    if (h < 15) return "12-15";
    if (h < 18) return "15-18";
    return "18-24";
  }, []);

  const compute = React.useCallback((all) => {
    const base = scope === "solo" ? all.filter(isSolo) : all.filter(isIncluded);

    const now = new Date();
    const monthsBack = period === "all" ? null : (Number(period) || 12);
    const cutoff = monthsBack ? new Date(now.getFullYear(), now.getMonth() - monthsBack + 1, 1) : null;
    const filterFn = (f, { ignorePeriod } = { ignorePeriod: false }) => {
      const d = f.service_date || f.created_at;
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return false;
      if (startDate && String(d).slice(0, 10) < startDate) return false;
      if (endDate && String(d).slice(0, 10) > endDate) return false;
      if (!ignorePeriod && cutoff && !(dt >= cutoff)) return false;

      const feeNum = Number(f.service_fee);
      const hasFee = !isNaN(feeNum) && feeNum > 0;
      if (!includeNoFee && !hasFee) return false;
      if (minFee && !(hasFee && feeNum >= Number(minFee))) return false;
      if (maxFee && !(hasFee && feeNum <= Number(maxFee))) return false;

      const pr = f.payment_received;
      const note = String(f.payment_note || "").toLowerCase();
      const paidHeuristic = (pr === true) || (pr == null && hasFee) || (note.includes("alındı") || note.includes("ödeme") || note.includes("odeme"));
      if (payFilter === "paid" && !paidHeuristic) return false;
      if (payFilter === "unpaid" && paidHeuristic) return false;

      if (followFilter !== "all") {
        const v = !!f.follow_up_needed;
        if (followFilter === "yes" && !v) return false;
        if (followFilter === "no" && v) return false;
      }
      if (deliveredFilter !== "all") {
        const v = !!f.delivered_working;
        if (deliveredFilter === "yes" && !v) return false;
        if (deliveredFilter === "no" && v) return false;
      }
      return true;
    };
    const filtered = base.filter((f) => filterFn(f, { ignorePeriod: false }));
    const filteredAllMonths = base.filter((f) => filterFn(f, { ignorePeriod: true }));
    const monthMap = new Map(); // month => count
    const feeMap = new Map(); // month => sumFee
    const minuteMap = new Map(); // month => sumMinutes
    const payMap = new Map(); // month => {paid,total}
    const surveyMap = new Map(); // month => {sum,count}
    const opMap = new Map(); // label => count
    const customerMap = new Map(); // name => count
    const projectMap = new Map(); // name => count
    const weekdayMap = new Map([["Pazartesi", 0], ["Salı", 0], ["Çarşamba", 0], ["Perşembe", 0], ["Cuma", 0], ["Cumartesi", 0], ["Pazar", 0]]);
    const hourMap = new Map([["00-06", 0], ["06-09", 0], ["09-12", 0], ["12-15", 0], ["15-18", 0], ["18-24", 0]]);
    const teamMapLocal = new Map(); // name => count
    const reasonMapLocal = new Map(); // unpaid reason => count
    const opDurMap = new Map(); // op => {sumMin, count}
    const devDurMap = new Map(); // device => {sumMin, count}
    let sumQ1 = 0, sumQ2 = 0, sumQ3 = 0, sumQ4 = 0;
    let countQ1 = 0, countQ2 = 0, countQ3 = 0, countQ4 = 0;

    let forms = 0;
    let paid = 0;
    let unpaid = 0;
    let feeSum = 0;
    let feeCount = 0;
    let minutesSum = 0;
    let minutesCount = 0;
    let followCount = 0;
    let deliveredCount = 0;
    let surveySum = 0;
    let surveyCount = 0;
    let lowSurveyCount = 0;

    const buildMonthlyMinutes = (list) => {
      const m = new Map();
      list.forEach((f) => {
        const minutes = parseMinutes(f);
        if (minutes == null) return;
        const key = toMonthKey(f.service_date || f.created_at) || "Unknown";
        m.set(key, (m.get(key) || 0) + minutes);
      });
      return m;
    };

    const tableRows = [];

    filtered.forEach((f) => {
      forms += 1;
      const d = f.service_date || f.created_at;
      const dt = new Date(d);
      const month = toMonthKey(d) || "Unknown";
      monthMap.set(month, (monthMap.get(month) || 0) + 1);

      const feeNum = Number(f.service_fee);
      const hasFee = !isNaN(feeNum) && feeNum > 0;
      if (hasFee) {
        feeMap.set(month, (feeMap.get(month) || 0) + feeNum);
        feeSum += feeNum;
        feeCount += 1;
      }

      const pr = f.payment_received;
      const note = String(f.payment_note || "").toLowerCase();
      const paidHeuristic = (pr === true) || (pr == null && hasFee) || (note.includes("alındı") || note.includes("ödeme") || note.includes("odeme"));
      if (paidHeuristic) paid += 1; else unpaid += 1;
      const pm = payMap.get(month) || { paid: 0, total: 0 };
      pm.total += 1;
      if (paidHeuristic) pm.paid += 1;
      payMap.set(month, pm);

      const minutes = parseMinutes(f);
      if (minutes != null) {
        minuteMap.set(month, (minuteMap.get(month) || 0) + minutes);
        minutesSum += minutes;
        minutesCount += 1;
      }

      const hb = hourBucketLabel(f.service_start_time);
      if (hb) hourMap.set(hb, (hourMap.get(hb) || 0) + 1);
      const wd = weekdayLabel(dt);
      if (wd) weekdayMap.set(wd, (weekdayMap.get(wd) || 0) + 1);

      if (f.follow_up_needed) followCount += 1;
      if (f.delivered_working) deliveredCount += 1;

      const avg = getSurveyAvg(f);
      if (typeof avg === "number" && !isNaN(avg)) {
        const rec = surveyMap.get(month) || { sum: 0, count: 0 };
        rec.sum += avg;
        rec.count += 1;
        surveyMap.set(month, rec);
        surveySum += avg;
        surveyCount += 1;
        if (avg < 3) lowSurveyCount += 1;
      }

      const ops = f.operation_types || {};
      Object.keys(ops).forEach((k) => {
        if (!ops[k]) return;
        const label = opLabelForKey(k);
        opMap.set(label, (opMap.get(label) || 0) + 1);
      });

      const customer = String(f.customer_name || "").trim() || "Bilinmiyor";
      customerMap.set(customer, (customerMap.get(customer) || 0) + 1);
      const project = String(f.project_name || "").trim() || "Bilinmiyor";
      projectMap.set(project, (projectMap.get(project) || 0) + 1);

      // Team members
      if (Array.isArray(f.team) && f.team.length) {
        f.team.forEach(t => {
          if (t && t.name && String(t.id) !== String(techId)) {
            teamMapLocal.set(t.name, (teamMapLocal.get(t.name) || 0) + 1);
          }
        });
      }

      // Unpaid Reasons
      if (!paidHeuristic) {
        const n = note.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const r = (n.includes("tanidik") || n.includes("es") || n.includes("dost")) ? "Tanıdık"
          : (n.includes("garanti") || n.includes("kapsam") || n.includes("teminat")) ? "Garanti kapsamında"
            : (n.includes("teknik") || n.includes("ariza")) ? "Teknik sorun"
              : "Diğer";
        reasonMapLocal.set(r, (reasonMapLocal.get(r) || 0) + 1);
      }

      // Device & Operation Durations
      const dev = String(f.device_type || "Bilinmiyor");
      if (minutes != null) {
        const drec = devDurMap.get(dev) || { sumMin: 0, count: 0 };
        drec.sumMin += minutes;
        drec.count += 1;
        devDurMap.set(dev, drec);

        Object.keys(ops).forEach((k) => {
          if (!ops[k]) return;
          const label = opLabelForKey(k);
          const orec = opDurMap.get(label) || { sumMin: 0, count: 0 };
          orec.sumMin += minutes;
          orec.count += 1;
          opDurMap.set(label, orec);
        });
      }

      // Specific Q averages
      const s = f.survey || {};
      if (typeof s.q1 === "number" && s.q1 > 0) { sumQ1 += s.q1; countQ1 += 1; }
      if (typeof s.q2 === "number" && s.q2 > 0) { sumQ2 += s.q2; countQ2 += 1; }
      if (typeof s.q3 === "number" && s.q3 > 0) { sumQ3 += s.q3; countQ3 += 1; }
      if (typeof s.q4 === "number" && s.q4 > 0) { sumQ4 += s.q4; countQ4 += 1; }


      tableRows.push({
        id: f.id,
        service_date: String(f.service_date || f.created_at || "").slice(0, 10),
        serial_number: String(f.serial_number || f.serial || "").trim(),
        customer_name: customer,
        project_name: project,
        fee: hasFee ? feeNum : 0,
        paid: paidHeuristic,
        follow: !!f.follow_up_needed,
        delivered: !!f.delivered_working,
        minutes: minutes != null ? minutes : null,
        surveyAvg: avg != null ? (Math.round(avg * 10) / 10) : null,
      });
    });

    const monthKeys = Array.from(new Set([...Array.from(monthMap.keys()), ...Array.from(feeMap.keys()), ...Array.from(minuteMap.keys())])).sort();
    setMonthlyForms(monthKeys.map((m) => ({ month: m, count: monthMap.get(m) || 0 })));
    setMonthlyFees(monthKeys.map((m) => ({ month: m, total: Math.round((feeMap.get(m) || 0)) })));
    setMonthlyHours(monthKeys.map((m) => ({ month: m, hours: Math.round(((minuteMap.get(m) || 0) / 60) * 10) / 10 })));
    setMonthlyPayRate(monthKeys.map((m) => {
      const r = payMap.get(m) || { paid: 0, total: 0 };
      return { month: m, rate: r.total ? Math.round((r.paid / r.total) * 100) : 0 };
    }));
    setMonthlySurveyAvg(monthKeys.map((m) => {
      const r = surveyMap.get(m) || { sum: 0, count: 0 };
      return { month: m, avg: r.count ? Math.round((r.sum / r.count) * 10) / 10 : 0 };
    }));
    setWeekdayDist(["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"].map((name) => ({ name, count: weekdayMap.get(name) || 0 })));
    setHourBucketDist(["00-06", "06-09", "09-12", "12-15", "15-18", "18-24"].map((name) => ({ name, count: hourMap.get(name) || 0 })));

    const opArr = Array.from(opMap.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
    setOpTypes(opArr);
    setTopCustomers(Array.from(customerMap.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 12));
    setTopProjects(Array.from(projectMap.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 12));
    setFormsTable(tableRows.sort((a, b) => String(b.service_date || "").localeCompare(String(a.service_date || ""))));
    setTeamMapArr(Array.from(teamMapLocal.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count));
    setUnpaidReasons(Array.from(reasonMapLocal.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count));
    setOpAvgTime(Array.from(opDurMap.entries()).map(([name, r]) => ({ name, avg: Math.round(r.sumMin / r.count) })).sort((a, b) => b.avg - a.avg));
    setDeviceTime(Array.from(devDurMap.entries()).map(([name, r]) => ({ name, avg: Math.round(r.sumMin / r.count), count: r.count })).sort((a, b) => b.count - a.count));
    setQAverages({
      q1: countQ1 ? (sumQ1 / countQ1) : 0,
      q2: countQ2 ? (sumQ2 / countQ2) : 0,
      q3: countQ3 ? (sumQ3 / countQ3) : 0,
      q4: countQ4 ? (sumQ4 / countQ4) : 0,
    });

    const hours = minutesSum / 60;
    const avgMinutes = minutesCount ? (minutesSum / minutesCount) : 0;
    const avgFee = feeCount ? (feeSum / feeCount) : 0;
    const formsPerHour = hours > 0 ? (forms / hours) : 0;
    const tryPerHour = hours > 0 ? (feeSum / hours) : 0;
    const payRate = forms ? Math.round((paid / forms) * 100) : 0;
    const followRate = forms ? Math.round((followCount / forms) * 100) : 0;
    const deliveredRate = forms ? Math.round((deliveredCount / forms) * 100) : 0;
    const surveyAvg = surveyCount ? (surveySum / surveyCount) : 0;

    setSummary({
      forms,
      hours: Math.round(hours * 10) / 10,
      avgMinutes: Math.round(avgMinutes),
      formsPerHour: Math.round(formsPerHour * 100) / 100,
      feeSum: Math.round(feeSum),
      avgFee: Math.round(avgFee),
      tryPerHour: Math.round(tryPerHour),
      paid,
      unpaid,
      payRate,
      followCount,
      followRate,
      deliveredCount,
      deliveredRate,
      surveyAvg: Math.round(surveyAvg * 10) / 10,
      lowSurveyCount,
    });

    const totalAll = buildMonthlyMinutes(filteredAllMonths);
    const allMin = Array.from(totalAll.values()).reduce((a, b) => a + (b || 0), 0);
    const selectedMin = minutesSum;
    return {
      allHours: Math.round((allMin / 60) * 10) / 10,
      selectedHours: Math.round((selectedMin / 60) * 10) / 10,
    };
  }, [scope, period, startDate, endDate, payFilter, deliveredFilter, followFilter, includeNoFee, minFee, maxFee, parseMinutes, hourBucketLabel, weekdayLabel, getSurveyAvg, opLabelForKey, isIncluded, isSolo]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      const t = await listTechnicians();
      if (!alive) return;
      setTechs(t);
      if (t.length && !techId) setTechId(String(t[0].id));
    })();
    return () => { alive = false; };
  }, [techId]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!techId) return;
      const all = await listForms({ technician: techId, page_size: 5000 });
      if (!alive) return;
      setPrimaryForms(all);
    })();
    return () => { alive = false; };
  }, [techId]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (scope !== "included") return;
      if (allForms.length) return;
      const all = await listForms({ page_size: 10000 });
      if (!alive) return;
      setAllForms(all);
    })();
    return () => { alive = false; };
  }, [scope, allForms.length]);

  React.useEffect(() => {
    const data = scope === "included" ? [...primaryForms, ...allForms] : primaryForms;
    if (!data || !data.length) {
      compute([]);
      return;
    }
    const unique = [];
    const seen = new Set();
    for (const f of data) {
      const id = f?.id != null ? String(f.id) : null;
      if (!id) {
        unique.push(f);
        continue;
      }
      if (seen.has(id)) continue;
      seen.add(id);
      unique.push(f);
    }
    compute(unique);
  }, [scope, allForms, primaryForms, compute]);

  const periodLabel =
    period === "all"
      ? "Tüm zamanlar"
      : (period === "1"
        ? "Son 1 ay"
        : (period === "3"
          ? "Son çeyrek (1-3 ay)"
          : `Son ${period} ay`));

  const pageBg = useColorModeValue("gray.50", "gray.900");
  const headerBg = useColorModeValue("linear(to-r, white, blue.50)", "linear(to-r, gray.800, blue.900)");
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.100", "gray.700");
  const mutedText = useColorModeValue("gray.600", "gray.400");
  const cursorFill = useColorModeValue("rgba(0,0,0,0.05)", "rgba(255,255,255,0.05)");
  const trackBg = useColorModeValue("gray.100", "gray.700");
  const tabsHeaderBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const tabItemBg = useColorModeValue("white", "whiteAlpha.100");
  const donutCenterColor = useColorModeValue("gray.800", "white");

  const hasActiveFilters = Boolean(
    startDate ||
    endDate ||
    payFilter !== "all" ||
    deliveredFilter !== "all" ||
    followFilter !== "all" ||
    minFee ||
    maxFee ||
    includeNoFee === false
  );

  const activeFilterCount = [
    Boolean(startDate),
    Boolean(endDate),
    payFilter !== "all",
    deliveredFilter !== "all",
    followFilter !== "all",
    Boolean(minFee),
    Boolean(maxFee),
    includeNoFee === false,
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setStartDate("");
    setEndDate("");
    setPayFilter("all");
    setDeliveredFilter("all");
    setFollowFilter("all");
    setIncludeNoFee(true);
    setMinFee("");
    setMaxFee("");
  };

  const fmt0 = React.useCallback((v) => {
    const n = Number(v);
    return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(isNaN(n) ? 0 : n);
  }, []);

  const fmt1 = React.useCallback((v) => {
    const n = Number(v);
    return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(isNaN(n) ? 0 : n);
  }, []);

  const Card = ({ children, ...props }) => (
    <Box
      bg={cardBg}
      borderRadius="3xl"
      p={{ base: 5, md: 6 }}
      shadow="md"
      borderWidth="1px"
      borderColor={cardBorder}
      transition="all 0.2s ease"
      _hover={{ shadow: "lg" }}
      {...props}
    >
      {children}
    </Box>
  );

  const KpiCard = ({ label, value, helper, accent = "blue.500", icon }) => {
    const iconBg = useColorModeValue(`${accent.split(".")[0]}.50`, `${accent.split(".")[0]}.900`);
    const valueColor = useColorModeValue("gray.800", "white");
    return (
      <Box
        bg={cardBg}
        borderRadius="2xl"
        p={{ base: 5, md: 6 }}
        shadow="md"
        borderWidth="1px"
        borderColor={cardBorder}
        transition="all 0.2s ease"
        _hover={{ shadow: "lg", transform: "translateY(-2px)" }}
      >
        <HStack align="center" justify="space-between" mb={4}>
          <Text fontWeight="medium" color={mutedText} fontSize="sm" textTransform="uppercase" letterSpacing="wider">
            {label}
          </Text>
          <Box w="48px" h="48px" borderRadius="full" bg={iconBg} display="flex" alignItems="center" justifyContent="center">
            {icon ? <Icon as={icon} color={accent} boxSize={5} /> : null}
          </Box>
        </HStack>
        <Text fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold" color={valueColor}>
          {value}
        </Text>
        {helper ? (
          <Text mt={2} fontSize="sm" color={mutedText}>
            {helper}
          </Text>
        ) : null}
      </Box>
    );
  };

  const copyReport = async () => {
    const opTop = opTypes.slice(0, 5).map((x) => `${x.name}: ${x.count}`).join("\n");
    const custTop = topCustomers.slice(0, 5).map((x) => `${x.name}: ${x.count}`).join("\n");
    const projTop = topProjects.slice(0, 5).map((x) => `${x.name}: ${x.count}`).join("\n");
    const txt = [
      `Teknisyen: ${currentTechName || "-"}`,
      `Dönem: ${periodLabel}`,
      hasActiveFilters ? `Ek Filtreler: ${activeFilterCount} aktif` : `Ek Filtreler: yok`,
      "",
      `Form: ${summary.forms}`,
      `Toplam saat: ${fmt1(summary.hours)} saat`,
      `Ort. süre: ${fmt0(summary.avgMinutes)} dk`,
      `Form/saat: ${fmt1(summary.formsPerHour)}`,
      "",
      `Ciro: ${fmt0(summary.feeSum)} ₺`,
      `Ort. ücret: ${fmt0(summary.avgFee)} ₺`,
      `₺/saat: ${fmt0(summary.tryPerHour)} ₺`,
      "",
      `Ödeme oranı: ${fmt0(summary.payRate)}% (Alındı: ${summary.paid}, Alınmadı: ${summary.unpaid})`,
      `Takip: ${fmt0(summary.followRate)}% (${summary.followCount})`,
      `Teslim: ${fmt0(summary.deliveredRate)}% (${summary.deliveredCount})`,
      "",
      `Anket ort.: ${fmt1(summary.surveyAvg)} (Düşük <3: ${summary.lowSurveyCount})`,
      "",
      "İşlem tipleri (Top 5):",
      opTop || "-",
      "",
      "Müşteriler (Top 5):",
      custTop || "-",
      "",
      "Projeler (Top 5):",
      projTop || "-",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(txt);
    } catch (_) { }
  };

  const downloadCsv = () => {
    const header = ["Tarih", "Seri No", "Müşteri", "Proje", "Ücret", "Ödeme", "Takip", "Teslim", "Süre(dk)", "Anket Ort."];
    const lines = formsTable.map((r) => [
      r.service_date || "",
      String(r.serial_number || "").replaceAll("\"", "\"\""),
      String(r.customer_name || "").replaceAll("\"", "\"\""),
      String(r.project_name || "").replaceAll("\"", "\"\""),
      String(r.fee ?? ""),
      r.paid ? "ALINDI" : "ALINMADI",
      r.follow ? "EVET" : "HAYIR",
      r.delivered ? "EVET" : "HAYIR",
      r.minutes == null ? "" : String(r.minutes),
      r.surveyAvg == null ? "" : String(r.surveyAvg),
    ]);
    const csv = [header, ...lines].map((row) => row.map((x) => `"${String(x ?? "").replaceAll("\"", "\"\"")}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `teknisyen_${currentTechName || "rapor"}_${String(new Date().toISOString()).slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box bg={pageBg} minH="100vh" py={{ base: 4, md: 6 }}>
      <Container maxW="1400px">
        <VStack align="stretch" spacing={5}>
          <Card bgGradient={headerBg}>
            <HStack justify="space-between" align={{ base: "start", md: "center" }} wrap="wrap" spacing={4}>
              <VStack align="start" spacing={2}>
                <HStack spacing={3} wrap="wrap">
                  <Heading size="md">Teknisyen Raporu</Heading>
                  {hasActiveFilters ? (
                    <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>
                      {activeFilterCount} filtre aktif
                    </Badge>
                  ) : (
                    <Badge colorScheme="green" borderRadius="full" px={3} py={1}>
                      Filtre yok
                    </Badge>
                  )}
                  {summary.lowSurveyCount > 0 ? (
                    <Badge colorScheme="red" borderRadius="full" px={3} py={1}>
                      <HStack spacing={1}>
                        <Icon as={FiAlertTriangle} />
                        <Text>Düşük anket: {summary.lowSurveyCount}</Text>
                      </HStack>
                    </Badge>
                  ) : null}
                </HStack>
                <Text fontSize="sm" color={mutedText}>
                  Teknisyen performansı • finans • operasyon • kalite
                </Text>
                <HStack spacing={2} wrap="wrap">
                  <Badge variant="subtle" borderRadius="full" px={3} py={1}>{currentTechName || "Teknisyen seç"}</Badge>
                  <Badge variant="subtle" borderRadius="full" px={3} py={1}>{periodLabel}</Badge>
                  <Badge variant="subtle" borderRadius="full" px={3} py={1}>
                    {scope === "solo" ? "Tek Gittiği" : "Bulunduğu (Sorumlu+Takım)"}
                  </Badge>
                  <HStack spacing={1}>
                    {["1", "3", "6", "12", "24", "all"].map((p) => (
                      <Button
                        key={p}
                        size="xs"
                        variant={period === p ? "solid" : "outline"}
                        colorScheme="blue"
                        onClick={() => setPeriod(p)}
                      >
                        {p === "all" ? "Tümü" : (p === "3" ? "Çeyrek" : `${p} ay`)}
                      </Button>
                    ))}
                  </HStack>
                </HStack>
              </VStack>
              <VStack align={{ base: "stretch", md: "end" }} spacing={3} w={{ base: "100%", md: "auto" }}>
                <HStack spacing={2} wrap="wrap" justify={{ base: "flex-start", md: "flex-end" }} w="100%">
                  <Box>
                    <Text fontSize="xs" color={mutedText} mb={1}>Teknisyen</Text>
                    <Select value={techId} onChange={(e) => setTechId(e.target.value)} w={{ base: "100%", sm: "260px" }} size="sm" bg={tabItemBg} borderColor={cardBorder}>
                      {techs.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </Select>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color={mutedText} mb={1}>Kapsam</Text>
                    <HStack spacing={1} bg={tabItemBg} border="1px solid" borderColor={cardBorder} borderRadius="xl" p={1}>
                      <Button size="sm" variant={scope === "included" ? "solid" : "ghost"} colorScheme="blue" onClick={() => setScope("included")}>
                        Bulunduğu
                      </Button>
                      <Button size="sm" variant={scope === "solo" ? "solid" : "ghost"} colorScheme="blue" onClick={() => setScope("solo")}>
                        Tek
                      </Button>
                    </HStack>
                  </Box>
                </HStack>
                <HStack spacing={2} wrap="wrap" justify={{ base: "flex-start", md: "flex-end" }}>
                  <Button leftIcon={<FiFilter />} onClick={() => setFiltersOpen(true)} colorScheme="blue" variant="outline" size="sm">
                    Filtreler
                  </Button>
                  <Button leftIcon={<FiCopy />} onClick={copyReport} variant="outline" size="sm">
                    Kopyala
                  </Button>
                  <Button leftIcon={<FiDownload />} onClick={downloadCsv} variant="outline" colorScheme="green" size="sm">
                    CSV
                  </Button>
                  <Button leftIcon={<FiArrowLeft />} onClick={() => navigate("/formlar/istatistik")} variant="ghost" size="sm">
                    Genel
                  </Button>
                </HStack>
              </VStack>
            </HStack>
          </Card>

          {hasActiveFilters ? (
            <Card>
              <HStack justify="space-between" align="start" wrap="wrap" spacing={3} mb={3}>
                <VStack align="start" spacing={0}>
                  <Heading size="sm">Aktif Filtreler</Heading>
                  <Text fontSize="xs" color={mutedText}>Bu sayfadaki tüm raporlar filtreye göre hesaplanır</Text>
                </VStack>
                <Button size="sm" variant="ghost" onClick={clearAllFilters}>Tümünü Temizle</Button>
              </HStack>
              <Box overflowX="auto">
                <Wrap>
                  {startDate ? (<Tag size="md" colorScheme="blue"><TagLabel>Bşl: {startDate}</TagLabel><TagCloseButton onClick={() => setStartDate("")} /></Tag>) : null}
                  {endDate ? (<Tag size="md" colorScheme="blue"><TagLabel>Btiş: {endDate}</TagLabel><TagCloseButton onClick={() => setEndDate("")} /></Tag>) : null}
                  {payFilter !== "all" ? (<Tag size="md" colorScheme={payFilter === "paid" ? "green" : "red"}><TagLabel>Ödeme: {payFilter === "paid" ? "Alındı" : "Alınmadı"}</TagLabel><TagCloseButton onClick={() => setPayFilter("all")} /></Tag>) : null}
                  {followFilter !== "all" ? (<Tag size="md" colorScheme="orange"><TagLabel>Takip: {followFilter === "yes" ? "Gerekli" : "Gerekli Değil"}</TagLabel><TagCloseButton onClick={() => setFollowFilter("all")} /></Tag>) : null}
                  {deliveredFilter !== "all" ? (<Tag size="md" colorScheme="teal"><TagLabel>Teslim: {deliveredFilter === "yes" ? "Çalışır" : "Çalışır Değil"}</TagLabel><TagCloseButton onClick={() => setDeliveredFilter("all")} /></Tag>) : null}
                  {(minFee || maxFee || includeNoFee === false) ? (
                    <Tag size="md" colorScheme="gray">
                      <TagLabel>Ücret: {minFee || 0} - {maxFee || "∞"} {includeNoFee ? "" : " (ücretsiz hariç)"}</TagLabel>
                      <TagCloseButton onClick={() => { setMinFee(""); setMaxFee(""); setIncludeNoFee(true); }} />
                    </Tag>
                  ) : null}
                </Wrap>
              </Box>
            </Card>
          ) : null}

          <Drawer isOpen={filtersOpen} placement="right" onClose={() => setFiltersOpen(false)}>
            <DrawerOverlay />
            <DrawerContent>
              <DrawerHeader borderBottomWidth="1px">Filtreler</DrawerHeader>
              <DrawerBody>
                <VStack align="stretch" spacing={4}>
                  <Select value={payFilter} onChange={(e) => setPayFilter(e.target.value)}>
                    <option value="all">Ödeme: Hepsi</option>
                    <option value="paid">Alındı</option>
                    <option value="unpaid">Alınmadı</option>
                  </Select>
                  <Select value={followFilter} onChange={(e) => setFollowFilter(e.target.value)}>
                    <option value="all">Takip: Hepsi</option>
                    <option value="yes">Gerekli</option>
                    <option value="no">Gerekli Değil</option>
                  </Select>
                  <Select value={deliveredFilter} onChange={(e) => setDeliveredFilter(e.target.value)}>
                    <option value="all">Teslim: Hepsi</option>
                    <option value="yes">Çalışır</option>
                    <option value="no">Çalışır Değil</option>
                  </Select>

                  <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>Tarih Aralığı</Text>
                    <HStack>
                      <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                      <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </HStack>
                  </Box>

                  <HStack>
                    <Input type="number" placeholder="Min ₺" value={minFee} onChange={(e) => setMinFee(e.target.value)} />
                    <Input type="number" placeholder="Max ₺" value={maxFee} onChange={(e) => setMaxFee(e.target.value)} />
                  </HStack>
                  <Checkbox isChecked={includeNoFee} onChange={(e) => setIncludeNoFee(e.target.checked)}>Ücreti olmayanları dahil et</Checkbox>
                </VStack>
              </DrawerBody>
              <DrawerFooter>
                <HStack w="100%" justify="space-between">
                  <Button variant="ghost" onClick={() => setFiltersOpen(false)}>Kapat</Button>
                  <HStack>
                    <Button variant="outline" onClick={clearAllFilters}>Temizle</Button>
                    <Button colorScheme="blue" onClick={() => setFiltersOpen(false)}>Uygula</Button>
                  </HStack>
                </HStack>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>

          <Box
            bg={cardBg}
            borderRadius="3xl"
            shadow="md"
            borderWidth="1px"
            borderColor={cardBorder}
            overflow="hidden"
          >
            <Tabs variant="unstyled" colorScheme="blue" isLazy>
              <Box px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }} borderBottomWidth="1px" borderColor={cardBorder} bg={tabsHeaderBg}>
                <HStack spacing={3} wrap="wrap">
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="bold">Paneller</Text>
                    <Text fontSize="xs" color={mutedText}>Teknisyen raporu: anlaşılır metrikler + trendler + detay tablo</Text>
                  </VStack>
                  <Spacer />
                  <TabList>
                    <HStack spacing={2} overflowX="auto" py={1}>
                      {[
                        { key: "ozet", label: "Özet", icon: FiTrendingUp },
                        { key: "finans", label: "Finans", icon: FiDollarSign },
                        { key: "operasyon", label: "Operasyon", icon: FiTool },
                        { key: "kalite", label: "Kalite", icon: FiUsers },
                        { key: "liste", label: "Formlar", icon: FiCheckCircle },
                      ].map((t) => (
                        <Tab
                          key={t.key}
                          px={3}
                          py={2}
                          borderRadius="xl"
                          bg={tabItemBg}
                          border="1px solid"
                          borderColor={cardBorder}
                          flexShrink={0}
                          transition="all 0.15s ease"
                          _hover={{ transform: "translateY(-1px)" }}
                          _selected={{ bg: "blue.500", color: "white", borderColor: "blue.500" }}
                        >
                          <HStack spacing={2}>
                            <Icon as={t.icon} />
                            <Text>{t.label}</Text>
                          </HStack>
                        </Tab>
                      ))}
                    </HStack>
                  </TabList>
                </HStack>
              </Box>
              <TabPanels>
                <TabPanel px={0}>
                  <Box px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }}>
                    <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
                      <KpiCard label="Form" value={fmt0(summary.forms)} helper={periodLabel} accent="blue.500" icon={FiTrendingUp} />
                      <KpiCard label="Toplam Saat" value={`${fmt1(summary.hours)} saat`} helper={`Ort. ${fmt0(summary.avgMinutes)} dk`} accent="cyan.500" icon={FiClock} />
                      <KpiCard label="Form/Saat" value={fmt1(summary.formsPerHour)} helper={`${fmt0(summary.tryPerHour)} ₺/saat`} accent="teal.500" icon={FiTrendingUp} />
                      <KpiCard label="Anket Ort." value={fmt1(summary.surveyAvg)} helper={`Düşük <3: ${fmt0(summary.lowSurveyCount)}`} accent={summary.surveyAvg >= 4 ? "green.500" : summary.surveyAvg >= 3 ? "yellow.500" : "red.500"} icon={FiUsers} />
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mt={6}>
                      <Card>
                        <Heading size="md" mb={2}>Takım Arkadaşları</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>En çok birlikte çalışılan teknisyenler (Form sayısı)</Text>
                        <VStack align="stretch" spacing={4}>
                          {teamMapArr.length ? teamMapArr.slice(0, 5).map(t => {
                            const pct = (t.count / Math.max(...teamMapArr.map(x => x.count))) * 100;
                            return (
                              <Box key={t.name}>
                                <HStack justify="space-between" mb={1}>
                                  <HStack spacing={2} minW={0}><Icon as={FiUsers} color={mutedText} /><Text fontWeight="medium" fontSize="sm" noOfLines={1}>{t.name}</Text></HStack>
                                  <Text fontWeight="bold" fontSize="sm">{t.count}</Text>
                                </HStack>
                                <Box w="100%" bg={trackBg} borderRadius="full" h="8px" overflow="hidden"><Box w={`${pct}%`} bg="blue.400" h="100%" borderRadius="full" /></Box>
                              </Box>
                            )
                          }) : <Text color={mutedText}>Sadece bireysel çalışmış veya veri yok.</Text>}
                        </VStack>
                      </Card>
                      <Card>
                        <Heading size="md" mb={2}>Takip & Teslim</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>Genel iş sonuçlandırma oranları</Text>
                        <VStack align="stretch" spacing={6} justify="center" h="100%">
                          <Box>
                            <HStack justify="space-between" mb={1}>
                              <Text fontWeight="medium" fontSize="sm">Takip Gerektiren Formlar</Text>
                              <Text fontWeight="bold" fontSize="sm">{summary.followRate}% ({summary.followCount})</Text>
                            </HStack>
                            <Box w="100%" bg={trackBg} borderRadius="full" h="12px" overflow="hidden"><Box w={`${summary.followRate}%`} bg="orange.400" h="100%" borderRadius="full" /></Box>
                          </Box>
                          <Box>
                            <HStack justify="space-between" mb={1}>
                              <Text fontWeight="medium" fontSize="sm">Çalışır Durumda Teslim Edilen</Text>
                              <Text fontWeight="bold" fontSize="sm">{summary.deliveredRate}% ({summary.deliveredCount})</Text>
                            </HStack>
                            <Box w="100%" bg={trackBg} borderRadius="full" h="12px" overflow="hidden"><Box w={`${summary.deliveredRate}%`} bg="teal.400" h="100%" borderRadius="full" /></Box>
                          </Box>
                        </VStack>
                      </Card>
                    </SimpleGrid>

                    <Divider my={6} borderColor={cardBorder} />

                    <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
                      <Card>
                        <Heading size="md" mb={2}>Aylık Form Trendi</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>Ay bazında form adedi</Text>
                        <ResponsiveContainer width="100%" height={260}>
                          <AreaChart data={monthlyForms}>
                            <defs>
                              <linearGradient id="colorForms" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3182CE" stopOpacity={0.5} />
                                <stop offset="95%" stopColor="#3182CE" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dy={10} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dx={-10} />
                            <Tooltip contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }} cursor={{ fill: cursorFill }} />
                            <Area type="monotone" dataKey="count" name="Form" stroke="#3182CE" strokeWidth={3} fillOpacity={1} fill="url(#colorForms)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Card>
                      <Card>
                        <Heading size="md" mb={2}>Aylık Saat Trendi</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>Ay bazında toplam çalışma saati</Text>
                        <ResponsiveContainer width="100%" height={260}>
                          <AreaChart data={monthlyHours}>
                            <defs>
                              <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#2B6CB0" stopOpacity={0.5} />
                                <stop offset="95%" stopColor="#2B6CB0" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dy={10} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dx={-10} />
                            <Tooltip formatter={(v) => [`${fmt1(v)} saat`, "Saat"]} contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }} cursor={{ fill: cursorFill }} />
                            <Area type="monotone" dataKey="hours" name="Saat" stroke="#2B6CB0" strokeWidth={3} fillOpacity={1} fill="url(#colorHours)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Card>
                      <Card>
                        <Heading size="md" mb={2}>Ödeme Durumu</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>Alındı / alınmadı</Text>
                        <Box position="relative" height="260px" display="flex" alignItems="center" justifyContent="center">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={[
                                  { name: "Alındı", value: summary.paid },
                                  { name: "Alınmadı", value: summary.unpaid },
                                ]}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={70}
                                outerRadius={100}
                                paddingAngle={4}
                                stroke="none"
                              >
                                <Cell fill="#38A169" />
                                <Cell fill="#E53E3E" />
                              </Pie>
                              <Tooltip contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }} />
                              <Legend iconType="circle" />
                            </PieChart>
                          </ResponsiveContainer>
                          <VStack position="absolute" pointerEvents="none" spacing={0}>
                            <Text fontSize="3xl" fontWeight="bold" color={donutCenterColor}>{fmt0(summary.payRate)}%</Text>
                            <Text fontSize="xs" color={mutedText}>Tahsilat Oranı</Text>
                          </VStack>
                        </Box>
                      </Card>
                    </SimpleGrid>
                  </Box>
                </TabPanel>

                <TabPanel px={0}>
                  <Box px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }}>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} mb={6}>
                      <KpiCard label="Ciro" value={`${fmt0(summary.feeSum)} ₺`} helper={`Ort. ${fmt0(summary.avgFee)} ₺`} accent="purple.500" icon={FiDollarSign} />
                      <KpiCard label="Ödeme %" value={`${fmt0(summary.payRate)}%`} helper={`Alındı: ${summary.paid} • Alınmadı: ${summary.unpaid}`} accent="green.500" icon={FiCheckCircle} />
                      <KpiCard label="₺ / Saat" value={`${fmt0(summary.tryPerHour)} ₺`} helper={`Form/saat: ${fmt1(summary.formsPerHour)}`} accent="teal.500" icon={FiTrendingUp} />
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mb={6}>
                      <Card gridColumn={{ lg: "span 2" }}>
                        <Heading size="md" mb={2}>Ödeme Alınmama Nedenleri</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>Tahsilat yapılamayan işlemlerin genel sebepleri</Text>
                        <VStack align="stretch" spacing={4}>
                          {unpaidReasons.length ? unpaidReasons.map((r, i) => {
                            const colors = ["red.400", "orange.400", "yellow.400", "gray.400"];
                            const pct = (r.count / Math.max(...unpaidReasons.map(x => x.count))) * 100;
                            return (
                              <Box key={r.name}>
                                <HStack justify="space-between" mb={1}>
                                  <Text fontWeight="medium" fontSize="sm">{r.name}</Text>
                                  <Text fontWeight="bold" fontSize="sm">{r.count}</Text>
                                </HStack>
                                <Box w="100%" bg={trackBg} borderRadius="full" h="8px" overflow="hidden"><Box w={`${pct}%`} bg={colors[i % colors.length]} h="100%" borderRadius="full" /></Box>
                              </Box>
                            )
                          }) : <Text color={mutedText}>Veri yok.</Text>}
                        </VStack>
                      </Card>
                      <Card gridColumn={{ lg: "span 1" }}>
                        <Heading size="md" mb={2}>Tahsilat Dağılımı</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>Ödenen vs Ödenmeyen</Text>
                        <Box position="relative" height="200px" display="flex" alignItems="center" justifyContent="center">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie data={[{ name: "Alındı", value: summary.paid }, { name: "Alınmadı", value: summary.unpaid }]} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} stroke="none">
                                <Cell fill="#38A169" />
                                <Cell fill="#E53E3E" />
                              </Pie>
                              <Tooltip contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }} />
                            </PieChart>
                          </ResponsiveContainer>
                          <VStack position="absolute" pointerEvents="none" spacing={0}>
                            <Text fontSize="2xl" fontWeight="bold" color={donutCenterColor}>{fmt0(summary.payRate)}%</Text>
                          </VStack>
                        </Box>
                      </Card>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                      <Card>
                        <Heading size="md" mb={2}>Aylık Ciro</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>Ay bazında toplam ücret</Text>
                        <ResponsiveContainer width="100%" height={300}>
                          <AreaChart data={monthlyFees}>
                            <defs>
                              <linearGradient id="colorFees" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#805AD5" stopOpacity={0.45} />
                                <stop offset="95%" stopColor="#805AD5" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dy={10} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dx={-10} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 100) / 10}k` : v)} />
                            <Tooltip formatter={(v) => [`${fmt0(v)} ₺`, "Ciro"]} contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }} cursor={{ fill: cursorFill }} />
                            <Area type="monotone" dataKey="total" name="Ciro" stroke="#805AD5" strokeWidth={3} fillOpacity={1} fill="url(#colorFees)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Card>
                      <Card>
                        <Heading size="md" mb={2}>Aylık Tahsilat Oranı</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>Ay bazında ödeme alınma oranı (%)</Text>
                        <ResponsiveContainer width="100%" height={300}>
                          <AreaChart data={monthlyPayRate}>
                            <defs>
                              <linearGradient id="colorPay" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#38A169" stopOpacity={0.45} />
                                <stop offset="95%" stopColor="#38A169" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dy={10} />
                            <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dx={-10} tickFormatter={(v) => `${v}%`} />
                            <Tooltip formatter={(v) => [`${fmt0(v)}%`, "Tahsilat %"]} contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }} cursor={{ fill: cursorFill }} />
                            <Area type="monotone" dataKey="rate" name="Tahsilat %" stroke="#38A169" strokeWidth={3} fillOpacity={1} fill="url(#colorPay)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Card>
                    </SimpleGrid>
                  </Box>
                </TabPanel>

                <TabPanel px={0}>
                  <Box px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }}>
                    <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
                      <Box bg={useColorModeValue("blue.50", "blue.900")} p={4} borderRadius="2xl" textAlign="center">
                        <Text color={useColorModeValue("blue.600", "blue.200")} fontSize="sm" mb={1} fontWeight="medium">Memnuniyet (Q1)</Text>
                        <Text fontSize="3xl" fontWeight="bold" color={useColorModeValue("blue.500", "blue.300")}>{fmt1(qAverages.q1)}</Text>
                      </Box>
                      <Box bg={useColorModeValue("green.50", "green.900")} p={4} borderRadius="2xl" textAlign="center">
                        <Text color={useColorModeValue("green.600", "green.200")} fontSize="sm" mb={1} fontWeight="medium">İletişim (Q2)</Text>
                        <Text fontSize="3xl" fontWeight="bold" color={useColorModeValue("green.500", "green.300")}>{fmt1(qAverages.q2)}</Text>
                      </Box>
                      <Box bg={useColorModeValue("orange.50", "orange.900")} p={4} borderRadius="2xl" textAlign="center">
                        <Text color={useColorModeValue("orange.600", "orange.200")} fontSize="sm" mb={1} fontWeight="medium">Zamanında (Q3)</Text>
                        <Text fontSize="3xl" fontWeight="bold" color={useColorModeValue("orange.500", "orange.300")}>{fmt1(qAverages.q3)}</Text>
                      </Box>
                      <Box bg={useColorModeValue("purple.50", "purple.900")} p={4} borderRadius="2xl" textAlign="center">
                        <Text color={useColorModeValue("purple.600", "purple.200")} fontSize="sm" mb={1} fontWeight="medium">Çözüm (Q4)</Text>
                        <Text fontSize="3xl" fontWeight="bold" color={useColorModeValue("purple.500", "purple.300")}>{fmt1(qAverages.q4)}</Text>
                      </Box>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
                      <Card>
                        <Heading size="md" mb={2}>Cihaz Başına Ortalama Süre</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>Hangi cihazda ortalama kaç dakika harcanıyor?</Text>
                        <VStack align="stretch" spacing={4} maxH="420px" overflowY="auto" pr={2}>
                          {deviceTime.length ? deviceTime.slice(0, 10).map((d) => {
                            const pct = (d.avg / Math.max(...deviceTime.map((x) => x.avg))) * 100;
                            return (
                              <Box key={d.name}>
                                <HStack justify="space-between" mb={1}>
                                  <HStack spacing={2} minW={0}><Icon as={FiTool} color={mutedText} /><Text fontWeight="medium" fontSize="sm" noOfLines={1} title={d.name}>{d.name}</Text></HStack>
                                  <Text fontWeight="bold" fontSize="sm">{d.avg} dk <Text as="span" fontWeight="normal" color={mutedText}>({d.count} form)</Text></Text>
                                </HStack>
                                <Box w="100%" bg={trackBg} borderRadius="full" h="8px" overflow="hidden"><Box w={`${pct}%`} bg="cyan.400" h="100%" borderRadius="full" /></Box>
                              </Box>
                            );
                          }) : <Text color={mutedText}>Veri yok</Text>}
                        </VStack>
                      </Card>

                      <Card>
                        <Heading size="md" mb={2}>İşlem Tipi Ort. Süre</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>İşlem başına harcanan ortalama dakikalar</Text>
                        <VStack align="stretch" spacing={4} maxH="420px" overflowY="auto" pr={2}>
                          {opAvgTime.length ? opAvgTime.slice(0, 10).map((o) => {
                            const pct = (o.avg / Math.max(...opAvgTime.map((x) => x.avg))) * 100;
                            return (
                              <Box key={o.name}>
                                <HStack justify="space-between" mb={1}>
                                  <HStack spacing={2} minW={0}><Icon as={FiClock} color={mutedText} /><Text fontWeight="medium" fontSize="sm" noOfLines={1}>{o.name}</Text></HStack>
                                  <Text fontWeight="bold" fontSize="sm">{o.avg} dk</Text>
                                </HStack>
                                <Box w="100%" bg={trackBg} borderRadius="full" h="8px" overflow="hidden"><Box w={`${pct}%`} bg="purple.400" h="100%" borderRadius="full" /></Box>
                              </Box>
                            );
                          }) : <Text color={mutedText}>Veri yok</Text>}
                        </VStack>
                      </Card>

                      <Card>
                        <Heading size="md" mb={2}>İşlem Tipi Yoğunluğu</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>Yazım hataları otomatik birleştirilir</Text>
                        <VStack align="stretch" spacing={4} maxH="420px" overflowY="auto" pr={2}>
                          {opTypes.length ? opTypes.slice(0, 14).map((o) => {
                            const max = Math.max(...opTypes.map((x) => x.count));
                            const pct = max > 0 ? (o.count / max) * 100 : 0;
                            return (
                              <Box key={o.name}>
                                <HStack justify="space-between" mb={1}>
                                  <HStack spacing={2} minW={0}>
                                    <Icon as={FiTool} color={mutedText} />
                                    <Text fontWeight="medium" fontSize="sm" noOfLines={1} title={o.name}>{o.name}</Text>
                                  </HStack>
                                  <Text fontWeight="bold" fontSize="sm">{o.count}</Text>
                                </HStack>
                                <Box w="100%" bg={trackBg} borderRadius="full" h="8px" overflow="hidden">
                                  <Box w={`${pct}%`} bg="purple.400" h="100%" borderRadius="full" />
                                </Box>
                              </Box>
                            );
                          }) : <Text color={mutedText}>Veri yok</Text>}
                        </VStack>
                      </Card>
                      <Card>
                        <Heading size="md" mb={2}>Gün / Saat Dağılımı</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>Hangi günler ve saatlerde yoğun?</Text>
                        <VStack align="stretch" spacing={6}>
                          <Box>
                            <Text fontSize="sm" fontWeight="bold" mb={2}>Haftanın günleri</Text>
                            <ResponsiveContainer width="100%" height={200}>
                              <BarChart data={weekdayDist}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dx={-10} />
                                <Tooltip contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }} cursor={{ fill: cursorFill }} />
                                <Bar dataKey="count" name="Adet" fill="#2B6CB0" radius={[8, 8, 0, 0]} barSize={32} />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box>
                            <Text fontSize="sm" fontWeight="bold" mb={2}>Saat aralığı</Text>
                            <ResponsiveContainer width="100%" height={200}>
                              <BarChart data={hourBucketDist}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dx={-10} />
                                <Tooltip contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }} cursor={{ fill: cursorFill }} />
                                <Bar dataKey="count" name="Adet" fill="#DD6B20" radius={[8, 8, 0, 0]} barSize={36} />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </VStack>
                      </Card>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                      <Card>
                        <Heading size="md" mb={2}>Müşteriler</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>En sık gidilen ilk 12</Text>
                        <VStack align="stretch" spacing={4}>
                          {topCustomers.length ? topCustomers.map((c) => {
                            const max = Math.max(...topCustomers.map((x) => x.count));
                            const pct = max > 0 ? (c.count / max) * 100 : 0;
                            return (
                              <Box key={c.name}>
                                <HStack justify="space-between" mb={1}>
                                  <HStack spacing={2} minW={0}>
                                    <Icon as={FiUsers} color={mutedText} />
                                    <Text fontWeight="medium" fontSize="sm" noOfLines={1} title={c.name}>{c.name}</Text>
                                  </HStack>
                                  <Text fontWeight="bold" fontSize="sm">{c.count}</Text>
                                </HStack>
                                <Box w="100%" bg={trackBg} borderRadius="full" h="8px" overflow="hidden">
                                  <Box w={`${pct}%`} bg="blue.400" h="100%" borderRadius="full" />
                                </Box>
                              </Box>
                            );
                          }) : <Text color={mutedText}>Veri yok</Text>}
                        </VStack>
                      </Card>
                      <Card>
                        <Heading size="md" mb={2}>Projeler</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>En sık gidilen ilk 12</Text>
                        <VStack align="stretch" spacing={4}>
                          {topProjects.length ? topProjects.map((p) => {
                            const max = Math.max(...topProjects.map((x) => x.count));
                            const pct = max > 0 ? (p.count / max) * 100 : 0;
                            return (
                              <Box key={p.name}>
                                <HStack justify="space-between" mb={1}>
                                  <HStack spacing={2} minW={0}>
                                    <Icon as={FiTool} color={mutedText} />
                                    <Text fontWeight="medium" fontSize="sm" noOfLines={1} title={p.name}>{p.name}</Text>
                                  </HStack>
                                  <Text fontWeight="bold" fontSize="sm">{p.count}</Text>
                                </HStack>
                                <Box w="100%" bg={trackBg} borderRadius="full" h="8px" overflow="hidden">
                                  <Box w={`${pct}%`} bg="teal.400" h="100%" borderRadius="full" />
                                </Box>
                              </Box>
                            );
                          }) : <Text color={mutedText}>Veri yok</Text>}
                        </VStack>
                      </Card>
                    </SimpleGrid>
                  </Box>
                </TabPanel>

                <TabPanel px={0}>
                  <Box px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }}>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} mb={6}>
                      <KpiCard label="Takip" value={`${fmt0(summary.followRate)}%`} helper={`${summary.followCount} form`} accent="orange.500" icon={FiTrendingUp} />
                      <KpiCard label="Teslim" value={`${fmt0(summary.deliveredRate)}%`} helper={`${summary.deliveredCount} form`} accent="teal.500" icon={FiCheckCircle} />
                      <KpiCard label="Düşük Anket" value={fmt0(summary.lowSurveyCount)} helper={`Ortalama: ${fmt1(summary.surveyAvg)}`} accent="red.500" icon={FiAlertTriangle} />
                    </SimpleGrid>
                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                      <Card>
                        <Heading size="md" mb={2}>Aylık Anket Ortalaması</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>Ay bazında ortalama anket puanı (1-5)</Text>
                        <ResponsiveContainer width="100%" height={280}>
                          <AreaChart data={monthlySurveyAvg}>
                            <defs>
                              <linearGradient id="colorSurvey" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#ECC94B" stopOpacity={0.45} />
                                <stop offset="95%" stopColor="#ECC94B" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dy={10} />
                            <YAxis domain={[0, 5]} axisLine={false} tickLine={false} tick={{ fill: mutedText, fontSize: 12 }} dx={-10} />
                            <Tooltip contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }} cursor={{ fill: cursorFill }} />
                            <Area type="monotone" dataKey="avg" name="Ortalama" stroke="#D69E2E" strokeWidth={3} fillOpacity={1} fill="url(#colorSurvey)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Card>
                      <Card>
                        <Heading size="md" mb={2}>Düşük Anketli Formlar</Heading>
                        <Text fontSize="sm" color={mutedText} mb={6}>Anket ortalaması 3 altı olan kayıtlar kırmızı görünür</Text>
                        <Box overflowX="auto">
                          <ModernTable
                            data={formsTable.filter((x) => x.surveyAvg != null && x.surveyAvg < 3).slice(0, 50)}
                            columns={[
                              { title: "Tarih", dataIndex: "service_date", sortable: true },
                              { title: "Seri No", dataIndex: "serial_number", sortable: true },
                              { title: "Müşteri", dataIndex: "customer_name", sortable: true },
                              { title: "Proje", dataIndex: "project_name", sortable: true },
                              { title: "Ortalama", dataIndex: "surveyAvg", sortable: true, render: (v) => (v == null ? "-" : fmt1(v)) },
                            ]}
                            searchable={true}
                            pageSize={10}
                            onRowClick={(row) => row?.id && navigate(`/formlar/${row.id}`)}
                            getRowProps={() => ({ bg: "red.50", _hover: { bg: "red.50", transform: "none" } })}
                          />
                        </Box>
                      </Card>
                    </SimpleGrid>
                  </Box>
                </TabPanel>

                <TabPanel px={0}>
                  <Box px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }}>
                    <Card>
                      <HStack justify="space-between" wrap="wrap" spacing={3} mb={4}>
                        <VStack align="start" spacing={0}>
                          <Heading size="md">Form Listesi</Heading>
                          <Text fontSize="sm" color={mutedText}>Filtrelenen tüm formlar • satıra tıklayınca detay açılır</Text>
                        </VStack>
                        <HStack>
                          <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>{fmt0(formsTable.length)} kayıt</Badge>
                        </HStack>
                      </HStack>
                      <ModernTable
                        data={formsTable}
                        getRowProps={(row) => {
                          if (row?.surveyAvg != null && row.surveyAvg < 3) return { bg: "red.50", _hover: { bg: "red.50", transform: "none" } };
                          return {};
                        }}
                        columns={[
                          { title: "Tarih", dataIndex: "service_date", sortable: true },
                          { title: "Seri No", dataIndex: "serial_number", sortable: true },
                          { title: "Müşteri", dataIndex: "customer_name", sortable: true },
                          { title: "Proje", dataIndex: "project_name", sortable: true },
                          { title: "Ücret (₺)", dataIndex: "fee", sortable: true, render: (v) => fmt0(v) },
                          { title: "Ödeme", dataIndex: "paid", sortable: true, render: (v) => (<HStack spacing={2}><Icon as={v ? FiCheckCircle : FiXCircle} color={v ? "green.500" : "red.500"} /><Text>{v ? "Alındı" : "Alınmadı"}</Text></HStack>) },
                          { title: "Takip", dataIndex: "follow", sortable: true, render: (v) => v ? "Evet" : "Hayır" },
                          { title: "Teslim", dataIndex: "delivered", sortable: true, render: (v) => v ? "Evet" : "Hayır" },
                          { title: "Süre (dk)", dataIndex: "minutes", sortable: true, render: (v) => (v == null ? "-" : fmt0(v)) },
                          { title: "Anket", dataIndex: "surveyAvg", sortable: true, render: (v) => (v == null ? "-" : fmt1(v)) },
                        ]}
                        searchable={true}
                        pageSize={15}
                        onRowClick={(row) => row?.id && navigate(`/formlar/${row.id}`)}
                      />
                    </Card>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
